// libs
import React, { useContext, useEffect } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";

// components
import {
    List,
    Stack,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    IconButton,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Divider,
} from "@mui/material";
import { Noop } from "@components/Noop";
import { MenuOpen } from "@mui/icons-material";
import { DrawerHeader } from "@components/navigation/components/DrawerHeader";

// store
import { AppContext } from "@store/context/App";
import { UserInfoContext } from "@store/context/UserInfo";

// constants
import { sideMenuItems } from "./SideMenuItems";
import { ALLOCATION_TYPES } from "@utils/constants";

// services
import { rumService } from "@services/rumService";
import { RUM_EVENT_TYPES, RUM_ACTIONS, RUM_COMPONENTS } from "@services/rumEventHelpers";

// styles
import {
    sideMenuItemIcon,
    sideMenuSandboxSelection,
    sideMenuAllocationTypeSelection,
    sideMenuItemText,
    sideMenuItemButton,
    sideMenu,
    sideMenuList,
    sideMenuItemDivider,
    sideMenuDrawer,
    sideMenuItem,
} from "./SideMenu.style";

// types
import {
    ALLOCATION_TYPE_PARAM,
    SANDBOX_ID_PARAM,
    SIMULATIONS_ROUTES,
    SIMULATION_ROUTES,
    getParamFromRouteQuery,
    isCurrentRouteMatching,
} from "../../../router";
import { IAllocationType } from "@defs/Utilities";
import { UserSandboxAccess } from "@utils/appsync/schema/API";
import useAppSettingsHandler from "@hooks/useAppSettingsHandler/useAppSettingsHandler";

const SideMenu: React.FC = () => {
    const { isSideMenuOpen, toggleSideMenu } = useContext(AppContext);
    const { getStageValue } = useAppSettingsHandler();

    useEffect(() => {
        getStageValue();
    }, []);

    const { sandboxes, selectedAllocationType, selectedSandbox, setAllocationType, setSelectedSandbox, username } = useContext(UserInfoContext);

    const navigate = useNavigate();

    const location = useLocation();

    useEffect(() => {
        if (location.search) {
            const sandboxId = getParamFromRouteQuery(location.search, SANDBOX_ID_PARAM);
            const allocationTypeInUrl = getParamFromRouteQuery(location.search, ALLOCATION_TYPE_PARAM);
            if (sandboxId) {
                setSelectedSandbox(sandboxes.filter((sandbox: UserSandboxAccess) => sandbox.sandbox_id === sandboxId)[0]);
            }
            if (allocationTypeInUrl) {
                setAllocationType(ALLOCATION_TYPES.filter((v: IAllocationType) => v.key === allocationTypeInUrl)[0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const navigateUser = (path?: string) => {
        return () =>
            path &&
            navigate({
                pathname: path,
                search: createSearchParams({
                    sandboxId: selectedSandbox?.sandbox_id,
                    allocationType: selectedAllocationType?.key,
                }).toString(),
            });
    };

    const updateRoute = (sandboxId?: string, allocationType?: string): void => {
        // if current route is simulation routes then redirect on Simulation History routes
        // in case of change in allcation type or sandbox.
        if (isCurrentRouteMatching(location.pathname, SIMULATION_ROUTES)) {
            navigate({
                pathname: SIMULATIONS_ROUTES[0].path,
                search: createSearchParams({
                    sandboxId: sandboxId ?? selectedSandbox.sandbox_id,
                    allocationType: allocationType ?? selectedAllocationType.key,
                }).toString(),
            });
        } else if (!location.search || location.search.indexOf(sandboxId) < 0 || location.search.indexOf(allocationType) < 0) {
            // If the sandbox id or allocation type is not present in the query parameter in the url or is different from the selected value:
            navigate({
                pathname: location.pathname,
                search: createSearchParams({
                    sandboxId: sandboxId ?? selectedSandbox.sandbox_id,
                    allocationType: allocationType ?? selectedAllocationType.key,
                }).toString(),
            });
        }
    };

    const renderAllocationTypeSelection = (): React.ReactElement => {
        const onSelectAllocationType = (selectedAllocationType: string): void => {
            updateRoute(selectedSandbox.sandbox_id, selectedAllocationType);
            setAllocationType(
                ALLOCATION_TYPES.filter((allocationType: IAllocationType) => allocationType.key === selectedAllocationType)[0],
            );
            rumService.recordBusinessEvent(
                RUM_EVENT_TYPES.ALLOCATION_TYPE_SELECTED,
                RUM_ACTIONS.ALLOC_TYPE_SELECT,
                RUM_COMPONENTS.APPLICATION,
                username,
                {
                    allocationType: selectedAllocationType,
                    sandboxId: selectedSandbox?.sandbox_id,
                },
            );
        };

        return (
            <FormControl size="small" sx={sideMenuAllocationTypeSelection}>
                <InputLabel id="allocation-type-input-label">Allocation Type</InputLabel>
                <Select
                    labelId="allocation-type-input-label"
                    label="Allocation type"
                    size="small"
                    value={selectedAllocationType?.key ?? ""}
                    onChange={(event) => onSelectAllocationType(event.target.value)}
                    fullWidth>
                    {ALLOCATION_TYPES.map((allocationType: IAllocationType) => (
                        <MenuItem value={allocationType.key} key={allocationType.key}>
                            {allocationType.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const renderSandboxSelection = (): React.ReactElement => {
        const onSelectSandbox = (selectedSandboxId: string): void => {
            updateRoute(selectedSandboxId);
            setSelectedSandbox(sandboxes.filter((sandbox) => sandbox.sandbox_id === selectedSandboxId)[0]);
            rumService.recordBusinessEvent(
                RUM_EVENT_TYPES.SANDBOX_SELECTED,
                RUM_ACTIONS.SANDBOX_SELECT,
                RUM_COMPONENTS.APPLICATION,
                username,
                {
                    sandboxId: selectedSandboxId,
                    sandboxName: sandboxes.find((s) => s.sandbox_id === selectedSandboxId)?.sandbox_name,
                },
            );
        };

        return (
            <FormControl size="small" sx={sideMenuSandboxSelection}>
                <InputLabel id="sandbox-input-label">Sandbox</InputLabel>
                <Select
                    labelId="sandbox-input-label"
                    label="Sandbox"
                    size="small"
                    value={selectedSandbox?.sandbox_id ?? ""}
                    onChange={(event: any) => onSelectSandbox(event.target.value)}
                    fullWidth>
                    {sandboxes.map((sandbox: UserSandboxAccess) => (
                        <MenuItem value={sandbox.sandbox_id} key={sandbox.sandbox_id}>
                            {sandbox.sandbox_name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const renderSideMenuList = (): React.ReactElement => {
        return (
            <List sx={sideMenuList}>
                {sideMenuItems.map(({ label, icon, path, heading, disabled, hide, divider }, index) => {
                    if (hide) return <Noop key={index + label} />;

                    return (
                        <>
                            <ListItem key={index + label} disablePadding sx={sideMenuItem(heading)}>
                                <ListItemButton
                                    disabled={!!disabled}
                                    disableRipple={!!heading}
                                    sx={sideMenuItemButton(isSideMenuOpen, heading)}
                                    onClick={navigateUser(path)}>
                                    {icon && <ListItemIcon sx={sideMenuItemIcon(heading)}>{icon}</ListItemIcon>}
                                    <ListItemText
                                        primary={label}
                                        primaryTypographyProps={
                                            heading
                                                ? {
                                                      fontSize: "14px",
                                                      fontWeight: "bold",
                                                  }
                                                : { fontSize: "14px" }
                                        }
                                        sx={sideMenuItemText(isSideMenuOpen, heading)}
                                    />
                                </ListItemButton>
                            </ListItem>
                            {divider && <Divider sx={sideMenuItemDivider} />}
                        </>
                    );
                })}
            </List>
        );
    };

    return (
        <Drawer open={isSideMenuOpen} anchor="left" variant="persistent" sx={sideMenuDrawer}>
            <DrawerHeader>
                <IconButton onClick={() => toggleSideMenu()}>
                    <MenuOpen />
                </IconButton>
            </DrawerHeader>
            <Stack direction="column" justifyContent="space-between" sx={sideMenu}>
                <Stack direction="column">
                    {renderSandboxSelection()}
                    {renderAllocationTypeSelection()}
                    {renderSideMenuList()}
                </Stack>
                {/* {open && (
                    <Stack direction="column">
                        <Divider sx={sideMenuItemDivider} />
                        <Typography sx={sideMenuSandboxControls}>Sandbox Controls</Typography>
                    </Stack>
                )} */}
            </Stack>
        </Drawer>
    );
};

export default React.memo(SideMenu);
